import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  PICKLE_BASES, REGIONAL_STYLES, TEXTURES, STUDIO_INGREDIENTS,
  JAR_SIZES, getLevelLabel, getSodiumEstimate,
} from "@/lib/constants";
import { Sparkles, Flame, Droplets, Citrus, Beef, Star, Leaf } from "lucide-react";

const TasteMeter = ({ spice, salt, oil, sourness }) => {
  const bars = [
    { label: "Spice", value: spice, color: "#9F3E18", icon: Flame },
    { label: "Salt", value: salt, color: "#2E5C5A", icon: Droplets },
    { label: "Sour", value: sourness, color: "#65A30D", icon: Citrus },
    { label: "Rich", value: oil, color: "#D97706", icon: Beef },
  ];
  return (
    <div className="space-y-3">
      {bars.map((bar) => (
        <div key={bar.label} className="flex items-center gap-3">
          <bar.icon size={14} style={{ color: bar.color }} className="shrink-0" />
          <div className="flex-1">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-sw-text-secondary">{bar.label}</span>
              <span className="text-[10px] text-sw-text-muted">{bar.value}%</span>
            </div>
            <div className="h-1.5 bg-cream-tertiary rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: bar.color }}
                initial={{ width: 0 }}
                animate={{ width: `${bar.value}%` }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const StudioPreview = (props) => {
  const { base, style, spice, salt, oil, sourness, texture, ingredients,
    activeProfile, jarSize, blendName, recipeStory, currentPrice,
    handleSave, handleAddToCart, mobileDrawer = false } = props;

  const baseInfo = PICKLE_BASES.find((b) => b.id === base);
  const styleInfo = REGIONAL_STYLES.find((s) => s.id === style);
  const textureInfo = TEXTURES.find((t) => t.id === texture);
  const jarInfo = JAR_SIZES.find((j) => j.id === jarSize);

  const activeIngredients = useMemo(() =>
    Object.entries(ingredients).filter(([, v]) => v).map(([k]) => STUDIO_INGREDIENTS.find((i) => i.id === k)?.name).filter(Boolean),
    [ingredients]
  );

  const wellnessBadges = useMemo(() => {
    const badges = [];
    if (salt <= 25) badges.push("Low Sodium");
    if (oil <= 30) badges.push("Low Fat");
    if (spice <= 25 && !ingredients.wholeChillies) badges.push("Kid-Safe");
    if (salt <= 20 && oil <= 30) badges.push("Heart-Smart");
    if (badges.length === 0) badges.push("Balanced");
    return badges;
  }, [salt, oil, spice, ingredients]);

  const chefRec = useMemo(() => {
    if (base === "mango" && style === "andhra" && spice >= 70) return "Your selections closely match the Classic Andhra Avakaya \u2014 our most popular creation.";
    if (spice <= 20) return "Consider adding a hint of jaggery to balance the mild spice profile.";
    if (oil <= 25) return "Low oil pickles are best enjoyed within 2 weeks for optimal freshness.";
    if (sourness >= 80) return "High tanginess pairs beautifully with a touch of sesame oil.";
    if (activeProfile) return `Optimized for ${activeProfile.name} preferences. You can still fine-tune any setting.`;
    return "Looking great! Your blend has a well-balanced flavor profile.";
  }, [base, style, spice, oil, sourness, activeProfile]);

  const similarity = useMemo(() => {
    if (base === "mango" && style === "andhra") return "Classic Andhra Avakaya";
    if (base === "lemon" && style === "tangy") return "Tangy Lemon Pickle";
    if (base === "greenChilli" && style === "fiery") return "Guntur Chilli Pickle";
    if (base === "garlic") return "Garlic Karam Podi";
    return null;
  }, [base, style]);

  return (
    <div className={`${mobileDrawer ? "" : "lg:sticky lg:top-32"} space-y-4`} data-testid="studio-preview">
      {/* Jar Visual Card */}
      <div className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm">
        <div className="relative flex h-44 items-center justify-center lg:h-52" style={{ background: `linear-gradient(135deg, ${baseInfo?.color}08, ${baseInfo?.color}18)` }}>
          <div className="relative">
            <motion.div
              key={base}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-20 h-28 lg:w-24 lg:h-32 rounded-2xl border-2 relative overflow-hidden"
              style={{ borderColor: baseInfo?.color + "40", backgroundColor: baseInfo?.color + "15" }}
            >
              {/* Jar fill level */}
              <div className="absolute bottom-0 w-full transition-all duration-700 rounded-b-xl" style={{ height: `${Math.max(20, oil)}%`, backgroundColor: baseInfo?.color + "35" }} />
              {/* Lid */}
              <div className="absolute top-0 w-full h-4 rounded-t-xl" style={{ backgroundColor: baseInfo?.color + "50" }} />
              {/* Spice dots */}
              {spice > 50 && <div className="absolute top-6 left-2 w-1.5 h-1.5 rounded-full bg-spice-terracotta/60" />}
              {spice > 70 && <div className="absolute top-8 right-3 w-1 h-1 rounded-full bg-spice-terracotta/50" />}
            </motion.div>
          </div>
          {/* Base label */}
          <div className="absolute bottom-3 left-4 right-4 flex justify-between items-end">
            <div>
              <p className="text-xs text-sw-text-muted">Base</p>
              <p className="text-sm font-heading font-600" style={{ color: baseInfo?.color }}>{baseInfo?.name}</p>
            </div>
            <p className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: baseInfo?.color + "15", color: baseInfo?.color }}>{styleInfo?.name}</p>
          </div>
        </div>

        {/* Name & Profile */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs text-sw-text-muted uppercase tracking-wider mb-0.5">Your Blend</p>
              <h3 className="text-lg font-heading font-700 text-sw-text leading-snug">{blendName}</h3>
            </div>
            {activeProfile && (
              <span className="shrink-0 text-[10px] font-medium px-2 py-1 rounded-full bg-earth-green/10 text-earth-green">{activeProfile.name}</span>
            )}
          </div>

          {mobileDrawer && (
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-2xl bg-cream-secondary px-3 py-3">
                <p className="text-[10px] uppercase tracking-[0.18em] text-sw-text-muted">Base</p>
                <p className="mt-1 text-sm font-medium text-sw-text">{baseInfo?.name}</p>
              </div>
              <div className="rounded-2xl bg-cream-secondary px-3 py-3">
                <p className="text-[10px] uppercase tracking-[0.18em] text-sw-text-muted">Style</p>
                <p className="mt-1 text-sm font-medium text-sw-text">{styleInfo?.name}</p>
              </div>
              <div className="rounded-2xl bg-cream-secondary px-3 py-3">
                <p className="text-[10px] uppercase tracking-[0.18em] text-sw-text-muted">Texture</p>
                <p className="mt-1 text-sm font-medium text-sw-text">{textureInfo?.name}</p>
              </div>
              <div className="rounded-2xl bg-cream-secondary px-3 py-3">
                <p className="text-[10px] uppercase tracking-[0.18em] text-sw-text-muted">Jar</p>
                <p className="mt-1 text-sm font-medium text-sw-text">{jarInfo?.name}</p>
              </div>
            </div>
          )}

          {/* Taste Meter */}
          <div className="mt-5">
            <p className="text-xs font-medium text-sw-text-muted uppercase tracking-wider mb-3">Taste Meter</p>
            <TasteMeter spice={spice} salt={salt} oil={oil} sourness={sourness} />
          </div>

          {/* Texture + Ingredients */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-sw-text-muted">Texture:</span>
              <span className="text-xs font-medium text-sw-text">{textureInfo?.name}</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {activeIngredients.map((name) => (
                <span key={name} className="text-[10px] bg-cream-secondary text-sw-text-secondary px-2 py-0.5 rounded-full">{name}</span>
              ))}
            </div>
          </div>

          {/* Wellness Badges */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-sw-text-muted uppercase tracking-wider mb-2">Wellness Snapshot</p>
            <div className="flex flex-wrap gap-1.5">
              {wellnessBadges.map((badge) => (
                <span key={badge} className="inline-flex items-center gap-1 text-[10px] font-medium bg-earth-green/8 text-earth-green px-2.5 py-1 rounded-full">
                  <Leaf size={10} />{badge}
                </span>
              ))}
            </div>
          </div>

          {/* Nutrition Snapshot */}
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-1.5">
            <p className="text-xs font-medium text-sw-text-muted uppercase tracking-wider mb-2">Nutrition Estimate</p>
            <div className="flex justify-between text-xs">
              <span className="text-sw-text-secondary">Est. Sodium</span>
              <span className="font-medium text-sw-text">{getSodiumEstimate(salt)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-sw-text-secondary">Oil Content</span>
              <span className="font-medium text-sw-text">{getLevelLabel(oil)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-sw-text-secondary">Spice Intensity</span>
              <span className="font-medium text-sw-text">{getLevelLabel(spice)}</span>
            </div>
          </div>

          {/* Chef's Recommendation */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={12} className="text-spice-mango" />
              <p className="text-xs font-medium text-sw-text-muted uppercase tracking-wider">Chef's Note</p>
            </div>
            <p className="text-xs text-sw-text-secondary leading-relaxed italic">{chefRec}</p>
            {similarity && (
              <p className="text-[10px] text-earth-green mt-2 font-medium">Closest to: {similarity}</p>
            )}
          </div>

          {/* Recipe Story */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-sw-text-muted uppercase tracking-wider mb-2">Recipe Story</p>
            <p className="text-xs text-sw-text-secondary leading-relaxed">{recipeStory}</p>
          </div>

          {mobileDrawer && (
            <div className="mt-5 border-t border-gray-100 pt-5 lg:hidden">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-sw-text-muted">
                    {jarInfo?.name} <span aria-hidden="true">&middot;</span> {jarInfo?.weight}
                  </p>
                  <p className="text-2xl font-heading font-800 text-sw-text">&#8377;{currentPrice}</p>
                </div>
                {props.isFavorite && <Star size={18} className="text-spice-mango fill-spice-mango" />}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleSave}
                  data-testid="preview-save-btn-mobile"
                  className="rounded-full border border-earth-green py-3 text-sm font-medium text-earth-green transition-all hover:bg-earth-green hover:text-white"
                >
                  Save
                </button>
                <button
                  onClick={handleAddToCart}
                  data-testid="preview-cart-btn-mobile"
                  className="rounded-full bg-earth-green py-3 text-sm font-medium text-white shadow-lg shadow-earth-green/20 transition-all hover:bg-earth-green-dark"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          )}

          {/* Price & CTA (desktop only) */}
          <div className="mt-5 pt-5 border-t border-gray-100 hidden lg:block">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs text-sw-text-muted">{jarInfo?.name} &middot; {jarInfo?.weight}</p>
                <p className="text-2xl font-heading font-800 text-sw-text">&#8377;{currentPrice}</p>
              </div>
              {props.isFavorite && <Star size={18} className="text-spice-mango fill-spice-mango" />}
            </div>
            <div className="flex gap-2">
              <button onClick={handleSave} data-testid="preview-save-btn" className="flex-1 border border-earth-green text-earth-green rounded-full py-2.5 text-sm font-medium hover:bg-earth-green hover:text-white transition-all">
                Save
              </button>
              <button onClick={handleAddToCart} data-testid="preview-cart-btn" className="flex-1 bg-earth-green text-white rounded-full py-2.5 text-sm font-medium hover:bg-earth-green-dark transition-all shadow-lg shadow-earth-green/20">
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
